import React, { useState } from "react";
import { Col, Row, Label, Button, Card, CardBody } from "reactstrap";
import { FiTag } from "react-icons/fi";
import FormInput from "../../formInput/FormInput";
import TextArea from "antd/es/input/TextArea";
import ReactQuill from "react-quill";
import { Spin } from "antd";
import { humanizeProductDescription } from "../../../api/products.api";
import toast from "react-hot-toast";
import { Tooltip } from "antd";
import { FaQuestionCircle } from "react-icons/fa";
import { formatAIResponseToHTML } from "../../../firebase/utility";


const Step1Identifiers = ({ newItem, setNewItem }) => {

    const [humanizing, setHumanizing] = useState(false);

    const handleHumanize = () => {
        if (!newItem?.long_description) {
            toast.error("Please provide a long description to humanize");
            return;
        }
        setHumanizing(true);
        humanizeProductDescription({ mfgPartNumber: newItem?.item_mfg_part, title: newItem?.item_title, description: newItem?.description, specifications: newItem?.item_specification, longDescription: newItem?.long_description }, (res) => {
            setHumanizing(false);
            if (res.status === 200) {
                const responseBody = res.data;
                const responseData = responseBody?.data || responseBody;

                const shortDesc = responseData?.short_description || responseData?.shortDescription;
                const longDesc = responseData?.long_description || responseData?.longDescription || (typeof responseData === 'string' ? responseData : "");


                const formattedLongDesc = formatAIResponseToHTML(longDesc);

                setNewItem(prev => {
                    const updated = {
                        ...prev,
                        long_description: formattedLongDesc || prev?.long_description,
                        description: shortDesc || prev?.description
                    };
                    return updated;
                });
                toast.success("Description humanized successfully");
            } else {
                toast.error(res.message || "Failed to humanize description");
            }
        });
    };

    return (
        <Card className="border-0 shadow-sm mb-4 bg-light">
            <CardBody className="p-4">
                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                        <FiTag size={16} />
                    </div>
                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: '13px' }}>Item Identifiers & Description</h6>
                </div>
                <Row className="g-2">
                    <Col md={3}>
                        <FormInput
                            type={"text"}
                            label={<span className="d-flex align-items-center gap-1">SKU * <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="Stock Keeping Unit - A unique identifier for the product"><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip></span>}
                            placeholder={"SKU"}
                            size={"large"}
                            value={newItem?.sku}
                            handleChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    sku: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={3}>
                        <FormInput
                            type={"text"}
                            label={<span className="d-flex align-items-center gap-1">Vendor Part <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="The part number used by the vendor or distributor"><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip></span>}
                            placeholder={"Vendor Part"}
                            size={"large"}
                            value={newItem?.vendor_part}
                            handleChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    vendor_part: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={3}>
                        <FormInput
                            type={"text"}
                            label={<span className="d-flex align-items-center gap-1">MFG Part * <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="The manufacturer's official part number"><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip></span>}
                            placeholder={"MFG Part"}
                            size={"large"}
                            value={newItem?.item_mfg_part}
                            handleChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    item_mfg_part: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={3}>
                        <FormInput
                            type={"text"}
                            label={<span className="d-flex align-items-center gap-1">UPC *<Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="This is compulsory - if there is no UPC available please enter n/a"><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip></span>}
                            placeholder={"UPC"}
                            size={"large"}
                            value={newItem?.item_upc}
                            handleChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    item_upc: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={6} className="d-flex flex-column">
                        <Label className="fw-bold mb-2">Title*</Label>
                        <TextArea
                            placeholder={"Enter Title"}
                            rows={4}
                            size={"large"}
                            style={{ height: '100%', resize: 'none' }}
                            value={newItem?.item_title}
                            onChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    item_title: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={6} className="d-flex flex-column">
                        <Label className="fw-bold mb-2">Description*</Label>
                        <TextArea
                            placeholder={"Enter Description"}
                            rows={4}
                            size={"large"}
                            style={{ height: '100%', resize: 'none' }}
                            value={newItem?.description}
                            onChange={(e) =>
                                setNewItem({
                                    ...newItem,
                                    description: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Label className="fw-bold mb-0">Long Description (Overview)</Label>
                            <Button
                                color="primary"
                                outline
                                size="sm"
                                className="py-1 px-3"
                                onClick={handleHumanize}
                                disabled={humanizing}
                            >
                                {humanizing ? <Spin size="small" /> : "Rewrite by AI"}
                            </Button>
                        </div>

                        <ReactQuill
                            theme="snow"
                            className="product-description-editor"
                            value={newItem?.long_description || ""}
                            onChange={(val) => {
                                setNewItem(prev => ({
                                    ...prev,
                                    long_description: val,
                                }));
                            }}
                        />
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default Step1Identifiers;
